import { apiRequest, ApiError } from '@/lib/api/client';
import { withResolvedAvatarUrl, withResolvedAvatarUrls } from '@/lib/api/avatar-url';
import { getAuthToken } from '@/lib/api/token-store';
import { mockFetchThreads, mockFetchThreadsOverview, mockThreadCount } from '@/lib/api/threads-mock';
import { MERGE_WA_GROUPS_PARAM, MERGE_WA_GROUPS_VALUE } from '@/lib/messages/merged-wa-groups';

// DEV-ONLY: `?mockThreads=N` serves N generated threads from threads-mock.js instead of the
// backend, for verifying the virtual thread list without a server. Compiled out of prod builds.
const mockActive = () => import.meta.env.DEV && mockThreadCount() > 0;

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || '').trim();
  if (!normalizedToken) {
    throw new ApiError('authorization token is required', 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

/**
 * List the current user's threads.
 * GET /api/threads[?scope=internal&search=&offset=&limit=&withTotal=&…filters&patientUserId=]
 *
 * scope "internal" returns the staff-only Chats threads (restricted to eligible user types);
 * omitting scope returns the normal Messages threads, which exclude internal chats. The normal
 * scope is offset-addressed over the stable {lastMessageAt desc, _id desc} sort: pass `offset`
 * to fetch any range, and `withTotal` to also get the exact thread count for the same filter —
 * the pair that backs the fully-virtual thread list. `search` queries across ALL threads as one
 * capped fetch (offset is ignored while searching).
 *
 * Filter params mirror the filter bar so the server total matches what the client renders:
 * `locked`, `awaitingReply`, `whatsapp` (conversationType), `whatsappBotId`,
 * `lastMessageFrom`/`lastMessageTo` (RFC3339 instants — resolve day bounds client-side), and
 * `groups` ("only" = grouped threads only, "exclude" = ungrouped tail; used by the Groups view).
 *
 * `patientUserId` narrows the list to threads that patient is ALSO a member of — the merged
 * WhatsApp view uses it to find a patient's other per-clinic threads, which are routinely absent
 * from the loaded ranges. Access is unchanged: it can only ever narrow what the caller sees.
 *
 * Returns { threads, page }, where page = { mode, offset, returned, total, truncated }.
 * `total` is null unless withTotal was set (or in search mode, where total == returned and
 * `truncated` marks a capped result).
 */
export async function fetchThreads({
  token,
  scope,
  search,
  offset,
  limit,
  withTotal,
  windowDays,
  patientUserId,
  locked,
  awaitingReply,
  whatsapp,
  // Generic channel filter (metadata.conversationType server-side, e.g. "app" for the Healo
  // mobile-app channel). Takes precedence over the legacy boolean `whatsapp` shorthand.
  conversationType,
  whatsappBotId,
  lastMessageFrom,
  lastMessageTo,
  groups,
  // Fold each WhatsApp group's per-clinic-gateway threads into one row (scope=groups only).
  mergeGroups,
  suppressPermissionDeniedEvent = false,
} = {}) {
  if (mockActive()) {
    const mocked = await mockFetchThreads({
      scope, search, offset, limit, withTotal, patientUserId,
      locked, awaitingReply, whatsapp, whatsappBotId, lastMessageFrom, lastMessageTo, groups,
      mergeGroups,
    });
    return { ...mocked, raw: mocked };
  }
  const params = new URLSearchParams();
  const normalizedScope = String(scope || '').trim();
  if (normalizedScope) params.set('scope', normalizedScope);
  const normalizedSearch = String(search || '').trim();
  if (normalizedSearch) params.set('search', normalizedSearch);
  if (Number.isFinite(offset) && offset >= 0) params.set('offset', String(Math.floor(offset)));
  if (Number.isFinite(limit) && limit > 0) params.set('limit', String(Math.floor(limit)));
  if (withTotal) params.set('withTotal', '1');
  if (Number.isFinite(windowDays) && windowDays >= 0) params.set('windowDays', String(Math.floor(windowDays)));
  const normalizedPatientUserId = String(patientUserId || '').trim();
  if (normalizedPatientUserId) params.set('patientUserId', normalizedPatientUserId);
  if (locked) params.set('locked', '1');
  if (awaitingReply) params.set('awaitingReply', '1');
  const normalizedConversationType = String(conversationType || '').trim().toLowerCase();
  if (normalizedConversationType) {
    params.set('conversationType', normalizedConversationType);
  } else if (whatsapp) {
    params.set('conversationType', 'whatsapp');
  }
  const normalizedBotId = String(whatsappBotId || '').trim();
  if (normalizedBotId) params.set('whatsappBotId', normalizedBotId);
  const normalizedFrom = String(lastMessageFrom || '').trim();
  if (normalizedFrom) params.set('lastMessageFrom', normalizedFrom);
  const normalizedTo = String(lastMessageTo || '').trim();
  if (normalizedTo) params.set('lastMessageTo', normalizedTo);
  const normalizedGroups = String(groups || '').trim();
  if (normalizedGroups) params.set('groups', normalizedGroups);
  if (mergeGroups) params.set(MERGE_WA_GROUPS_PARAM, MERGE_WA_GROUPS_VALUE);

  const qs = params.toString();
  const path = qs ? `/api/threads?${qs}` : '/api/threads';

  const payload = await apiRequest(path, {
    method: 'GET',
    headers: createAuthHeaders(token),
    suppressPermissionDeniedEvent,
  });

  const threads = withResolvedAvatarUrls(payload?.threads);
  const rawPage = payload?.page && typeof payload.page === 'object' ? payload.page : {};
  return {
    threads,
    page: {
      mode: rawPage.mode ? String(rawPage.mode) : null,
      offset: Number.isFinite(rawPage.offset) ? rawPage.offset : 0,
      returned: Number.isFinite(rawPage.returned) ? rawPage.returned : threads.length,
      // Defensive: a backend that predates the offset mode omits total — callers treat null as
      // "unknown" rather than zero.
      total: Number.isFinite(rawPage.total) ? rawPage.total : null,
      truncated: Boolean(rawPage.truncated),
    },
    raw: payload,
  };
}

/**
 * Threads overview: the first rows of the Messages list PLUS the complete attention sections,
 * plus the tenant-wide shared settings. With `withTotal`, page.total carries the exact
 * unfiltered thread count — the seed for the fully-virtual list. Call with windowDays: 0 so the
 * returned rows equal offset rows [0, limit) of the virtual list's sort.
 * GET /api/threads/overview[?limit=&windowDays=&withTotal=]
 *
 * attention.waiting holds every "Waiting for you" thread (locked; staff_handles is retired);
 * attention.patientReply every "Unanswered patient reply" CANDIDATE (non-locked, patient-last,
 * unacked) and attention.botAck every "Unanswered bot replies" CANDIDATE (non-locked, bot-last,
 * unacked) — for both the server applies the maxHours cutoff but not the thresholdMinutes lower
 * bound, which the caller applies with its clock so rows age into the section between refreshes.
 * patientReply and botAck have SEPARATE windows (a patient unanswered for days must not age out
 * on a bot's schedule); the server's single candidate query uses the wider of the two, so both
 * lists are complete for their own window. settings carries the shared thread groups and both
 * windows from global_settings. Rows have the same shape as fetchThreads rows.
 *
 * attention.truncated means the server's candidate cap was hit and the sections may be short —
 * surface it rather than silently rendering a partial triage list.
 */
export async function fetchThreadsOverview({ token, limit, windowDays, withTotal } = {}) {
  if (mockActive()) {
    const mocked = await mockFetchThreadsOverview({ limit, withTotal });
    return { ...mocked, raw: mocked };
  }
  const params = new URLSearchParams();
  if (Number.isFinite(limit) && limit > 0) params.set('limit', String(Math.floor(limit)));
  if (Number.isFinite(windowDays) && windowDays >= 0) params.set('windowDays', String(Math.floor(windowDays)));
  if (withTotal) params.set('withTotal', '1');

  const qs = params.toString();
  const path = qs ? `/api/threads/overview?${qs}` : '/api/threads/overview';

  const payload = await apiRequest(path, {
    method: 'GET',
    headers: createAuthHeaders(token),
  });

  const rawPage = payload?.page && typeof payload.page === 'object' ? payload.page : {};
  const rawAttention = payload?.attention && typeof payload.attention === 'object' ? payload.attention : {};
  const rawSettings = payload?.settings && typeof payload.settings === 'object' ? payload.settings : {};
  const rawBotAck = rawSettings.botAck && typeof rawSettings.botAck === 'object' ? rawSettings.botAck : {};
  const rawPatientReply =
    rawSettings.patientReply && typeof rawSettings.patientReply === 'object' ? rawSettings.patientReply : {};
  return {
    threads: withResolvedAvatarUrls(payload?.threads),
    // The per-user default assistant thread, surfaced regardless of the window so it can be pinned
    // to the top of the list.
    pinned: withResolvedAvatarUrls(payload?.pinned),
    page: {
      returned: Number.isFinite(rawPage.returned)
        ? rawPage.returned
        : Array.isArray(payload?.threads)
          ? payload.threads.length
          : 0,
      // Exact unfiltered thread count (withTotal only); null = unknown (older backend).
      total: Number.isFinite(rawPage.total) ? rawPage.total : null,
    },
    attention: {
      waiting: withResolvedAvatarUrls(rawAttention.waiting),
      patientReply: withResolvedAvatarUrls(rawAttention.patientReply),
      botAck: withResolvedAvatarUrls(rawAttention.botAck),
      truncated: Boolean(rawAttention.truncated),
    },
    settings: {
      threadGroups: Array.isArray(rawSettings.threadGroups) ? rawSettings.threadGroups : null,
      botAck: {
        thresholdMinutes: Number.isFinite(rawBotAck.thresholdMinutes) ? rawBotAck.thresholdMinutes : null,
        maxHours: Number.isFinite(rawBotAck.maxHours) ? rawBotAck.maxHours : null,
      },
      // null on an older backend that has no separate patient-reply window; callers fall back to
      // PATIENT_REPLY_DEFAULT_* rather than to bot-ack's, which is what caused the drop-outs.
      patientReply: {
        thresholdMinutes: Number.isFinite(rawPatientReply.thresholdMinutes)
          ? rawPatientReply.thresholdMinutes
          : null,
        maxHours: Number.isFinite(rawPatientReply.maxHours) ? rawPatientReply.maxHours : null,
      },
    },
    raw: payload,
  };
}

/**
 * Create (or, for 1:1s, find) an internal staff chat.
 * POST /api/threads/internal  body { name?, userIds, kind }
 *
 * kind is "direct" (exactly one partner, deduplicated) or "group" (named, multi-partner).
 * Returns { threadId, created } — created is false when an existing 1:1 thread was reused.
 */
export async function createInternalThread({ token, name, userIds, kind } = {}) {
  const ids = Array.isArray(userIds)
    ? userIds.map((id) => String(id || '').trim()).filter(Boolean)
    : [];
  if (!ids.length) {
    throw new ApiError('at least one participant is required', 400);
  }

  const body = { userIds: ids };
  const normalizedKind = String(kind || '').trim().toLowerCase();
  if (normalizedKind) {
    body.kind = normalizedKind;
  }
  const normalizedName = String(name || '').trim();
  if (normalizedName) {
    body.name = normalizedName;
  }

  const payload = await apiRequest('/api/threads/internal', {
    method: 'POST',
    headers: {
      ...createAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  return {
    threadId: String(payload?.threadId || '').trim(),
    created: Boolean(payload?.created),
    raw: payload,
  };
}

/**
 * Find or create a staff-initiated WhatsApp thread with a patient who hasn't messaged first.
 * POST /api/threads/patient  body { patientUserId, phone?, displayName?, whatsappBotId? }
 *
 * Binds the chosen WhatsApp gateway (whatsappBotId, else the default) and resolves the per-number
 * thread so the chat lands on the patient's number-specific conversation. Returns { threadId,
 * created } — created is false when an existing thread was reused.
 */
export async function ensurePatientThread({ token, patientUserId, phone, displayName, whatsappBotId } = {}) {
  const normalizedId = String(patientUserId || '').trim();
  const normalizedPhone = String(phone || '').trim();
  // Either identifier works: a phone-only request (a calendar new-patient booking or a
  // number typed into "New message") find-or-creates a patient by phone on the backend.
  if (!normalizedId && !normalizedPhone) {
    throw new ApiError('patientUserId or phone is required', 400);
  }
  const body = {};
  if (normalizedId) body.patientUserId = normalizedId;
  if (normalizedPhone) body.phone = normalizedPhone;
  const normalizedName = String(displayName || '').trim();
  if (normalizedName) body.displayName = normalizedName;
  const normalizedBotId = String(whatsappBotId || '').trim();
  if (normalizedBotId) body.whatsappBotId = normalizedBotId;

  const payload = await apiRequest('/api/threads/patient', {
    method: 'POST',
    headers: {
      ...createAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  return {
    threadId: String(payload?.threadId || '').trim(),
    created: Boolean(payload?.created),
    raw: payload,
  };
}

/**
 * Find-or-create a patient's Healo-app conversation (metadata.conversationType == "app") —
 * the app-channel sibling of ensurePatientThread, backing "New message → Healo app".
 * Requires an existing patient account (there is no phone fallback: the app channel only
 * reaches patients who signed in from the mobile app). Returns { threadId, created }.
 */
export async function ensureAppThreadForPatient({ token, patientUserId } = {}) {
  const normalizedId = String(patientUserId || '').trim();
  if (!normalizedId) {
    throw new ApiError('patientUserId is required', 400);
  }
  const payload = await apiRequest('/api/threads/app', {
    method: 'POST',
    headers: {
      ...createAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ patientUserId: normalizedId }),
  });
  return {
    threadId: String(payload?.threadId || '').trim(),
    created: Boolean(payload?.created),
    raw: payload,
  };
}

export async function fetchThreadHistory({
  token,
  threadId,
  maxMessageCount = 0,
  skip = 0,
  suppressPermissionDeniedEvent = false,
} = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }

  const query = new URLSearchParams({
    maxMessageCount: String(Number(maxMessageCount) || 0),
    skip: String(Number(skip) || 0),
  });

  const payload = await apiRequest(`/api/threads/${encodeURIComponent(normalizedThreadId)}/history?${query.toString()}`, {
    method: 'GET',
    headers: createAuthHeaders(token),
    suppressPermissionDeniedEvent,
  });

  const history = Array.isArray(payload?.history) ? payload.history : [];
  return {
    threadId: normalizedThreadId,
    history,
    raw: payload,
  };
}

function normalizeFlagPayload(flags) {
  const list = Array.isArray(flags) ? flags : [];
  const normalized = list.map((f) => String(f || "").trim()).filter(Boolean);
  if (!normalized.length) {
    throw new ApiError('at least one flag is required', 400);
  }
  return normalized;
}

/**
 * Add flags. `reason` (optional) is persisted to metadata.lockReason while a `locked` flag is
 * being added, so the UI can show "Locked · out_of_service"; the backend clears it on unlock.
 */
export async function addThreadFlags({ token, threadId, flags, reason } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const bodyFlags = normalizeFlagPayload(flags);
  const body = { flags: bodyFlags };
  const normalizedReason = String(reason || '').trim();
  if (normalizedReason) body.reason = normalizedReason;

  const payload = await apiRequest(`/api/threads/${encodeURIComponent(normalizedThreadId)}/flags:add`, {
    method: 'POST',
    headers: {
      ...createAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    flags: Array.isArray(payload?.flags) ? payload.flags : [],
    raw: payload,
  };
}

/**
 * Set or clear the thread's workflow-status override.
 * POST /api/threads/{threadId}/status  body { status, pendingReason?, pendingUntil? }
 *
 * status "" clears the override (the effective status falls back to the derived value).
 * For a snooze pass status "pending", pendingReason "snooze" and a pendingUntil ISO date.
 * The server stamps statusChangedAt/By and (for done/pending) statusMessageId — the anchor
 * the client uses to auto-reopen the thread on the next inbound patient message.
 */
export async function setThreadStatus({ token, threadId, status, pendingReason, pendingUntil } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const body = { status: String(status || '').trim() };
  const normalizedReason = String(pendingReason || '').trim();
  if (normalizedReason) body.pendingReason = normalizedReason;
  const normalizedUntil =
    pendingUntil instanceof Date ? pendingUntil.toISOString() : String(pendingUntil || '').trim();
  if (normalizedUntil) body.pendingUntil = normalizedUntil;

  const payload = await apiRequest(`/api/threads/${encodeURIComponent(normalizedThreadId)}/status`, {
    method: 'POST',
    headers: {
      ...createAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    status: String(payload?.status || '').trim(),
    statusChangedAt: payload?.statusChangedAt || null,
    pendingReason: String(payload?.pendingReason || '').trim(),
    pendingUntil: payload?.pendingUntil || null,
    raw: payload,
  };
}

/**
 * Triage-board threads: every enriched board candidate for the caller (AI-paused, status
 * override, or assigned; "done" bounded to today), newest first. The client groups rows into
 * columns with getEffectiveThreadStatus. truncated=true means the candidate cap was hit.
 * GET /api/threads/board
 */
export async function fetchBoardThreads({ token } = {}) {
  const payload = await apiRequest('/api/threads/board', {
    method: 'GET',
    headers: createAuthHeaders(token),
  });
  return {
    threads: withResolvedAvatarUrls(payload?.threads),
    truncated: Boolean(payload?.truncated),
    raw: payload,
  };
}

/**
 * Fetch a single thread as a standard enriched list row (same shape as fetchThreads rows:
 * title, patient tags, task counts, assignee, live last-message fields, avatarUrl). Resolves
 * a selected thread that is outside every loaded list — e.g. opened from the triage board,
 * the Tasks page, or a notification while beyond the list window/page.
 * GET /api/threads/{threadId}
 */
export async function fetchThread({ token, threadId, suppressPermissionDeniedEvent = false } = {}) {
  const tid = String(threadId || '').trim();
  if (!tid) {
    throw new ApiError('threadId is required', 400);
  }
  const payload = await apiRequest(`/api/threads/${encodeURIComponent(tid)}`, {
    method: 'GET',
    headers: createAuthHeaders(token),
    suppressPermissionDeniedEvent,
  });
  return payload?.thread && typeof payload.thread === 'object' ? withResolvedAvatarUrl(payload.thread) : null;
}

/**
 * Acknowledge a single message.
 * POST /api/threads/{threadId}/messages/{messageId}/ack
 *
 * Stamps `metadata.acknowledged` on the message so an unanswered bot reply drops out of the
 * "Unanswered bot replies" thread-list section. A later message re-triggers it.
 */
export async function acknowledgeMessage({ token, threadId, messageId } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const normalizedMessageId = String(messageId || '').trim();
  if (!normalizedMessageId) {
    throw new ApiError('messageId is required', 400);
  }

  const payload = await apiRequest(
    `/api/threads/${encodeURIComponent(normalizedThreadId)}/messages/${encodeURIComponent(normalizedMessageId)}/ack`,
    {
      method: 'POST',
      headers: {
        ...createAuthHeaders(token),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    },
  );

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    messageId: String(payload?.messageId || normalizedMessageId).trim(),
    acknowledgedAt: payload?.acknowledgedAt || null,
    raw: payload,
  };
}

/**
 * Set the shared "business" reaction on a message.
 * POST /api/threads/{threadId}/messages/{messageId}/reactions  body { emoji }
 *
 * Stores the reaction on the target message and, when the message belongs to a WhatsApp chat,
 * mirrors it onto WhatsApp via the bound bot. Replaces any existing business reaction.
 */
export async function setMessageReaction({ token, threadId, messageId, emoji } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const normalizedMessageId = String(messageId || '').trim();
  if (!normalizedMessageId) {
    throw new ApiError('messageId is required', 400);
  }
  const normalizedEmoji = String(emoji || '').trim();
  if (!normalizedEmoji) {
    throw new ApiError('emoji is required', 400);
  }

  const payload = await apiRequest(
    `/api/threads/${encodeURIComponent(normalizedThreadId)}/messages/${encodeURIComponent(normalizedMessageId)}/reactions`,
    {
      method: 'POST',
      headers: {
        ...createAuthHeaders(token),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ emoji: normalizedEmoji }),
    },
  );

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    messageId: String(payload?.messageId || normalizedMessageId).trim(),
    reactions: Array.isArray(payload?.reactions) ? payload.reactions : [],
    raw: payload,
  };
}

/**
 * Clear the shared "business" reaction on a message.
 * DELETE /api/threads/{threadId}/messages/{messageId}/reactions
 */
export async function removeMessageReaction({ token, threadId, messageId } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const normalizedMessageId = String(messageId || '').trim();
  if (!normalizedMessageId) {
    throw new ApiError('messageId is required', 400);
  }

  const payload = await apiRequest(
    `/api/threads/${encodeURIComponent(normalizedThreadId)}/messages/${encodeURIComponent(normalizedMessageId)}/reactions`,
    {
      method: 'DELETE',
      headers: createAuthHeaders(token),
    },
  );

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    messageId: String(payload?.messageId || normalizedMessageId).trim(),
    reactions: Array.isArray(payload?.reactions) ? payload.reactions : [],
    raw: payload,
  };
}

/**
 * Delete one of OUR WhatsApp messages for everyone — retract it from the patient's phone.
 * POST /api/threads/{threadId}/messages/{messageId}/revoke
 *
 * Returns 202: the message is struck through for every staff client right away, but whether
 * WhatsApp accepted the retraction is only known afterwards (it refuses past its ~2-day window),
 * and that verdict arrives as a message update — either settling the deletion or flagging that the
 * patient can still see it. Rejects with 409 when we already know it cannot work: the message has
 * not reached WhatsApp yet, it is not ours, or the WhatsApp connection is down.
 */
export async function revokeWhatsAppMessage({ token, threadId, messageId } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const normalizedMessageId = String(messageId || '').trim();
  if (!normalizedMessageId) {
    throw new ApiError('messageId is required', 400);
  }

  const payload = await apiRequest(
    `/api/threads/${encodeURIComponent(normalizedThreadId)}/messages/${encodeURIComponent(normalizedMessageId)}/revoke`,
    {
      method: 'POST',
      headers: createAuthHeaders(token),
    },
  );

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    messageId: String(payload?.messageId || normalizedMessageId).trim(),
    pending: payload?.pending === true,
    raw: payload,
  };
}

/**
 * Internal Chats "delete for everyone" (unsend) — one of YOUR OWN messages disappears for every
 * member; no tombstone, no undo. POST /api/threads/{threadId}/messages/{messageId}/unsend
 *
 * 200 {threadId, messageId, deleted:true}. 403 when not the author, 400 for system rows or a
 * non-internal thread, 404 when the message is already gone. Other clients drop the bubble from
 * the `{message:{…,deleted:true}, update:true}` broadcast.
 */
export async function unsendInternalMessage({ token, threadId, messageId } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const normalizedMessageId = String(messageId || '').trim();
  if (!normalizedMessageId) {
    throw new ApiError('messageId is required', 400);
  }

  const payload = await apiRequest(
    `/api/threads/${encodeURIComponent(normalizedThreadId)}/messages/${encodeURIComponent(normalizedMessageId)}/unsend`,
    {
      method: 'POST',
      headers: createAuthHeaders(token),
    },
  );

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    messageId: String(payload?.messageId || normalizedMessageId).trim(),
    deleted: payload?.deleted === true,
    raw: payload,
  };
}

/**
 * Edit a message's text. PUT /api/threads/{threadId}/messages/{messageId}  body {text, mentions?}
 *
 * Internal chat: 200 {threadId, messageId, message} — `message` is the full updated message (with
 * `metadata.edited`), also broadcast to every member. WhatsApp: 202 {threadId, messageId,
 * pending:true} — the verdict arrives later as a message update (editPending clears and
 * `metadata.edited` lands, or editFailed + editError). 409 when WhatsApp cannot: not on WhatsApp
 * yet, not ours, not text-only, older than 15 minutes, an edit already in flight, gateway down.
 * 400 for empty text / system rows, 403 when not the author (internal).
 *
 * `mentions` is only meaningful for internal chats: pass the re-derived array (an empty array
 * clears them); leave it undefined for WhatsApp so the server leaves metadata.mentions untouched.
 * An empty text is NOT rejected here — caption edits of attachment messages are the server's call.
 */
export async function editThreadMessage({ token, threadId, messageId, text, mentions } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const normalizedMessageId = String(messageId || '').trim();
  if (!normalizedMessageId) {
    throw new ApiError('messageId is required', 400);
  }
  const normalizedText = String(text ?? '').trim();
  const body = { text: normalizedText };
  if (Array.isArray(mentions)) {
    body.mentions = mentions.map((id) => String(id || '').trim()).filter(Boolean);
  }

  const payload = await apiRequest(
    `/api/threads/${encodeURIComponent(normalizedThreadId)}/messages/${encodeURIComponent(normalizedMessageId)}`,
    {
      method: 'PUT',
      headers: {
        ...createAuthHeaders(token),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    messageId: String(payload?.messageId || normalizedMessageId).trim(),
    pending: payload?.pending === true,
    message: payload?.message && typeof payload.message === 'object' ? payload.message : null,
    raw: payload,
  };
}

/**
 * DEV tool: soft-delete one message. The backend sets a deleted flag on the document (it is
 * never removed from the DB) and broadcasts the removal so connected clients drop the bubble.
 * DELETE /api/threads/{threadId}/messages/{messageId}
 */
export async function deleteThreadMessage({ token, threadId, messageId } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const normalizedMessageId = String(messageId || '').trim();
  if (!normalizedMessageId) {
    throw new ApiError('messageId is required', 400);
  }

  const payload = await apiRequest(
    `/api/threads/${encodeURIComponent(normalizedThreadId)}/messages/${encodeURIComponent(normalizedMessageId)}`,
    {
      method: 'DELETE',
      headers: createAuthHeaders(token),
    },
  );

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    messageId: String(payload?.messageId || normalizedMessageId).trim(),
    raw: payload,
  };
}

/**
 * DEV tool: "clear to this point" — soft-delete every message AFTER the given one (the
 * message itself survives as the thread's new last message). Documents are never removed
 * from the DB, only flagged; the bot reads filtered history so it no longer sees them.
 * POST /api/threads/{threadId}/messages/{messageId}/clearAfter
 */
export async function clearThreadMessagesAfter({ token, threadId, messageId } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const normalizedMessageId = String(messageId || '').trim();
  if (!normalizedMessageId) {
    throw new ApiError('messageId is required', 400);
  }

  const payload = await apiRequest(
    `/api/threads/${encodeURIComponent(normalizedThreadId)}/messages/${encodeURIComponent(normalizedMessageId)}/clearAfter`,
    {
      method: 'POST',
      headers: createAuthHeaders(token),
    },
  );

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    messageId: String(payload?.messageId || normalizedMessageId).trim(),
    deletedCount: Number(payload?.deletedCount) || 0,
    raw: payload,
  };
}

/**
 * Admin endpoint: update a thread's name and/or metadata.
 * PUT /api/admin/threads/{threadID}
 *
 * Per spec, sending `metadata` REPLACES the entire metadata object, so callers must
 * pass the full merged metadata they want persisted, not just the changed keys.
 * At least one of `name` or `metadata` must be provided.
 */
export async function updateAdminThread({ token, threadId, name, metadata } = {}) {
  const normalizedThreadId = String(threadId || "").trim();
  if (!normalizedThreadId) {
    throw new ApiError("threadId is required", 400);
  }

  const body = {};
  if (typeof name === "string") {
    body.name = name;
  }
  if (metadata !== undefined) {
    if (metadata === null || typeof metadata !== "object" || Array.isArray(metadata)) {
      throw new ApiError("metadata must be an object", 400);
    }
    body.metadata = metadata;
  }
  if (!("name" in body) && !("metadata" in body)) {
    throw new ApiError("provide at least one field to update", 400);
  }

  const payload = await apiRequest(
    `/api/admin/threads/${encodeURIComponent(normalizedThreadId)}`,
    {
      method: "PUT",
      headers: {
        ...createAuthHeaders(token),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    name: typeof payload?.name === "string" ? payload.name : "",
    metadata: payload?.metadata && typeof payload.metadata === "object" ? payload.metadata : {},
    raw: payload,
  };
}

// DISABLED (for now): thread assignment — assignment lives on tasks only (see
// lib/api/tasks.js updateTask with assigneeId). The backend routes are disabled
// too (server.go). Kept for easy restore.
//
// /**
//  * Assign a thread to a staff member (manual). Omitting `userId` assigns the
//  * thread to the calling user (used for take-over-on-send). Returns the refreshed
//  * thread so callers can splice the new assignee fields into local state.
//  * POST /api/threads/{threadID}/assign
//  */
// export async function assignThread({ token, threadId, userId } = {}) {
//   const normalizedThreadId = String(threadId || '').trim();
//   if (!normalizedThreadId) {
//     throw new ApiError('threadId is required', 400);
//   }
//   const body = {};
//   const normalizedUserId = String(userId || '').trim();
//   if (normalizedUserId) {
//     body.userId = normalizedUserId;
//   }
//
//   const payload = await apiRequest(`/api/threads/${encodeURIComponent(normalizedThreadId)}/assign`, {
//     method: 'POST',
//     headers: {
//       ...createAuthHeaders(token),
//       'Content-Type': 'application/json',
//     },
//     body: JSON.stringify(body),
//   });
//
//   return {
//     threadId: normalizedThreadId,
//     thread: payload?.thread && typeof payload.thread === 'object' ? payload.thread : null,
//     raw: payload,
//   };
// }
//
// /**
//  * Clear a thread's assignee.
//  * POST /api/threads/{threadID}/unassign
//  */
// export async function unassignThread({ token, threadId } = {}) {
//   const normalizedThreadId = String(threadId || '').trim();
//   if (!normalizedThreadId) {
//     throw new ApiError('threadId is required', 400);
//   }
//
//   const payload = await apiRequest(`/api/threads/${encodeURIComponent(normalizedThreadId)}/unassign`, {
//     method: 'POST',
//     headers: {
//       ...createAuthHeaders(token),
//       'Content-Type': 'application/json',
//     },
//   });
//
//   return {
//     threadId: normalizedThreadId,
//     thread: payload?.thread && typeof payload.thread === 'object' ? payload.thread : null,
//     raw: payload,
//   };
// }

/**
 * Soft-delete a thread (members only). Used by the Chats "Delete chat" action.
 * DELETE /api/threads/{threadId}
 */
export async function deleteThread({ token, threadId } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }

  const payload = await apiRequest(`/api/threads/${encodeURIComponent(normalizedThreadId)}`, {
    method: 'DELETE',
    headers: createAuthHeaders(token),
  });

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    isDeleted: Boolean(payload?.isDeleted),
    raw: payload,
  };
}

/**
 * Mark a thread read for the CALLER (moves their read watermark to now, zeroing the
 * row's unreadCount). Internal Chats unread tracking.
 * POST /api/threads/{threadId}/read
 */
export async function markThreadRead({ token, threadId } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const payload = await apiRequest(`/api/threads/${encodeURIComponent(normalizedThreadId)}/read`, {
    method: 'POST',
    headers: createAuthHeaders(token),
  });
  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    lastReadAt: String(payload?.lastReadAt || ''),
    raw: payload,
  };
}

/**
 * Flag a thread unread for the caller (count floors at 1 until the next mark-read).
 * POST /api/threads/{threadId}/unread
 */
export async function markThreadUnread({ token, threadId } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const payload = await apiRequest(`/api/threads/${encodeURIComponent(normalizedThreadId)}/unread`, {
    method: 'POST',
    headers: createAuthHeaders(token),
  });
  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    raw: payload,
  };
}

/**
 * Leave an internal GROUP chat (self-removal; appends the "left the group" system row).
 * POST /api/threads/{threadId}/leave
 */
export async function leaveInternalThread({ token, threadId } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const payload = await apiRequest(`/api/threads/${encodeURIComponent(normalizedThreadId)}/leave`, {
    method: 'POST',
    headers: createAuthHeaders(token),
  });
  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    left: Boolean(payload?.left),
    raw: payload,
  };
}

/**
 * The caller's unread summary for one thread scope: per-thread counts plus the number of
 * threads with unread messages. `scope: 'internal'` (the default, kept for the existing
 * callers) feeds the sidebar Chats bubble; `scope: 'groups'` feeds the WhatsApp Groups
 * lens badge.
 * GET /api/threads/unread-summary?scope=internal|groups
 */
export async function fetchThreadsUnreadSummary({
  token,
  scope = 'internal',
  suppressPermissionDeniedEvent = false,
} = {}) {
  const normalizedScope = String(scope || 'internal').trim() || 'internal';
  const payload = await apiRequest(
    `/api/threads/unread-summary?scope=${encodeURIComponent(normalizedScope)}`,
    {
      method: 'GET',
      headers: createAuthHeaders(token),
      suppressPermissionDeniedEvent,
    },
  );
  const threads = payload?.threads && typeof payload.threads === 'object' && !Array.isArray(payload.threads)
    ? payload.threads
    : {};
  const byThreadId = {};
  for (const [tid, count] of Object.entries(threads)) {
    const id = String(tid || '').trim();
    const n = Number(count) || 0;
    if (id && n > 0) byThreadId[id] = n;
  }
  return {
    total: Number(payload?.total) || 0,
    byThreadId,
    raw: payload,
  };
}

export async function removeThreadFlags({ token, threadId, flags } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const bodyFlags = normalizeFlagPayload(flags);

  const payload = await apiRequest(`/api/threads/${encodeURIComponent(normalizedThreadId)}/flags:remove`, {
    method: 'POST',
    headers: {
      ...createAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ flags: bodyFlags }),
  });

  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    flags: Array.isArray(payload?.flags) ? payload.flags : [],
    raw: payload,
  };
}
